import {
  Box,
  Button,
  chakra,
  Flex,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Checkbox,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";

import generateExcel from "zipcelx";
import {
  fuzzyFilter,
  isDateLessThanFilter,
  userFilter,
  arrayFilter,
} from "./filter";

import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { cloneElement, useState } from "react";

import {
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiFilter,
} from "react-icons/fi";

import { EmptyState, ErrorBoundary, LoadingSpinner, SelectButton, SelectList, SelectOption } from "@saas-ui/react";
import { useParams, useSearchParams } from "react-router-dom";
import { ColumnFilter, FilteredValues, GlobalFilter } from "./Filters";
import { Paginator } from "./Paginator";
import { downloadCsv, flattenData } from "../components/surveys/DownloadSurveyData";

const DataTable = ({
  data,
  columns,
  columnsToFilter = [],
  isLoading,
  handleLink,
  title = "",
  isExportable = true,
  isSelectable = false,
  simplify = false,
  isSearchable = true,
  otherComponents,
  children,
  exportOptions = ['xls'],
  ...props
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const initialFocusRef = React.useRef();
  const [rowSelection, setRowSelection] = useState({});


  const { tab, id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const table = useReactTable({
    columns: columns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuzzyFilter,
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: isSelectable,
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: 20 } },
    state: { globalFilter, rowSelection },
    filterFns: {
      fuzzy: fuzzyFilter,
      dateFilter: isDateLessThanFilter,
      user: userFilter,
      array: arrayFilter,
    },
    enableSortingRemoval: false,
  });

  function getExcel() {
    const config = {
      filename: `export.xlsx`,
      sheet: {
        data: [],
      },
    };

    const dataSet = config.sheet.data;

    // review with one level nested config
    // HEADERS
    table.getHeaderGroups().forEach((headerGroup) => {
      const headerRow = [];
      if (headerGroup.headers) {
        headerGroup.headers.forEach((column) => {
          headerRow.push({ value: column?.id });
        });
      }
      dataSet.push(headerRow);
    });

    // FILTERED ROWS
    table.getRowModel().rows.forEach((row) => {
      const dataRow = [];
      Object.values(row.getVisibleCells()).forEach((cell) => {
        dataRow.push({
          value: cell.getValue(),
        });
      });
      dataSet.push(dataRow);
    });

    return generateExcel(config);
  }

  function getFlattenedExcel() {
    const config = {
      filename: `export`,
      sheet: {
        data: [],
      },
    };

    const dataSet = config.sheet.data;
    const allData = table.getRowModel().rows.map((row) => row.original); // respects filtering/sorting
    const visibleColumns = table.getVisibleLeafColumns(); // Only visible columns

    const flatKeys = new Set();
    const nestedFieldMeta = {};

    visibleColumns.forEach((column) => {
      const accessorKey = column.columnDef.accessorKey;
      if (!accessorKey) return;

      allData.forEach((row) => {
        const value = row[accessorKey];

        if (Array.isArray(value) && value.every((v) => typeof v === "object")) {
          if (!nestedFieldMeta[accessorKey]) {
            nestedFieldMeta[accessorKey] = {
              keys: new Set(),
              maxLength: 0,
            };
          }
          nestedFieldMeta[accessorKey].maxLength = Math.max(
            nestedFieldMeta[accessorKey].maxLength,
            value.length
          );
          value.forEach((obj) => {
            Object.keys(obj).forEach((k) =>
              nestedFieldMeta[accessorKey].keys.add(k)
            );
          });
        } else {
          flatKeys.add(accessorKey);
        }
      });
    });

    const headerRow = [];

    flatKeys.forEach((key) => {
      headerRow.push({ value: key });
    });

    Object.entries(nestedFieldMeta).forEach(([field, meta]) => {
      const keys = Array.from(meta.keys);
      for (let i = 1; i <= meta.maxLength; i++) {
        keys.forEach((k) => {
          headerRow.push({ value: `${k}_${i}` });
        });
      }
    });

    dataSet.push(headerRow);

    allData.forEach((row) => {
      const dataRow = [];

      // flat values
      flatKeys.forEach((key) => {
        dataRow.push({ value: row[key] });
      });

      // nested values
      Object.entries(nestedFieldMeta).forEach(([field, meta]) => {
        const nestedArray = row[field] || [];
        for (let i = 0; i < meta.maxLength; i++) {
          const entry = nestedArray[i] || {};
          Array.from(meta.keys).forEach((key) => {
            dataRow.push({ value: entry[key] || "" });
          });
        }
      });

      dataSet.push(dataRow);
    });

    return generateExcel(config);
  }

  const handleDownload = (e) => {
    const format = e.currentTarget.value || e.currentTarget.id;
    if (format === "csv") {
      const flattened = flattenData(data);
      downloadCsv(flattened, `${tab}-data-${id}.csv`);
    }
    if (format === "xls") {
      getFlattenedExcel();
    }
  }

  return isLoading ? (
    <LoadingSpinner />
  ) : !!data ? (
    <Flex direction="column" height="100%" width="100%">
      {!simplify && (
        <HStack my="3" px="3" justifyContent="space-between">
          <HStack>
            {title && (
              <Heading ml="3" size="sm">
                {title}
              </Heading>
            )}
            {children}
            {columnsToFilter && (
              <>
                <Popover initialFocusRef={initialFocusRef} placement="bottom">
                  {({ isOpen, onClose }) => (
                    <Box ml={3} width="auto">
                      <PopoverTrigger>
                        <Button size="xs" variant="outline">
                          <FiFilter />
                          <Text ml="1">Filter</Text>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent width="100%">
                        <Tabs>
                          <TabList>
                            {columnsToFilter?.map((cc) => (
                              <Tab key={`tabh-${cc}`}>
                                <Text fontSize="sm">
                                  {table.getColumn(cc).columnDef?.header}
                                </Text>
                              </Tab>
                            ))}
                          </TabList>
                          <TabPanels>
                            {columnsToFilter?.map((cc) => (
                              <TabPanel key={`tab-${cc}`} p="0">
                                <ColumnFilter
                                  column={table.getColumn(cc)}
                                  table={table}
                                  close={onClose}
                                />
                              </TabPanel>
                            ))}
                          </TabPanels>
                        </Tabs>
                      </PopoverContent>
                    </Box>
                  )}
                </Popover>
              </>
            )}
          </HStack>
          <HStack>
            {isSearchable && (
              <GlobalFilter
                value={table.getState().globalFilter}
                filterFn={setGlobalFilter}
              />
            )}
            {/* {isExportable && (
              <Button onClick={() => getExcel()} variant="outline">
                <HStack m="3">
                  <FiDownload />
                  <Text mr=".5">Export</Text>
                </HStack>
              </Button>
            )} */}
            <Menu>
              <MenuButton
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                minWidth="100px"
                px={4}
                as={Button}
                variant="outline"
                rightIcon={<FiChevronDown />}>
                Export
              </MenuButton>
              <MenuList minWidth="100px">
                {exportOptions.map((format) => (
                  <MenuItem
                    key={format}
                    value={format}
                    onClick={handleDownload}
                  >
                    <HStack
                      align="start">
                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                        >
                          {format.toUpperCase()}
                        </Text>
                      </Box>
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {otherComponents && otherComponents}
          </HStack>
        </HStack>
      )}
      <HStack
        borderTop={simplify ? "0" : "1px"}
        color="gray.200"
        justifyContent="space-between"
      >
        <FilteredValues columnsToFilter={columnsToFilter} table={table} />
        <HStack>
          {props?.tableSummary && cloneElement(props?.tableSummary, { table })}
        </HStack>
      </HStack>
      <Box flex="auto" overflowY="auto">
        <Table size="sm" variant={simplify ? "unstyled" : "simple"}>
          <Thead borderTop={simplify ? "0" : "1px"} borderTopColor="gray.100">
            {table.getHeaderGroups()?.map((headerGroup) => (
              <Tr key={`hge-${headerGroup.id}`}>
                {headerGroup.headers?.map((header) => {
                  const meta = header.column.columnDef.meta;
                  const colName = header.column.id;
                  if (header.column.columnDef?.isHidden) return null;
                  return (
                    <Th
                      py="3"
                      pl="2"
                      key={`header-${header.id}`}
                      // onClick={header.column.getToggleSortingHandler()}
                      onClick={() => {
                        const currentSort = header.column.getIsSorted();
                        const columnId = header.column.id;

                        if (!currentSort) {
                          table.setSorting([{ id: columnId, desc: false }]);
                        } else {
                          header.column.toggleSorting(currentSort === "asc");
                        }
                      }}
                      isNumeric={meta?.isNumeric}
                    >
                      <HStack>
                        <Text
                          fontWeight="bold"
                          whiteSpace="nowrap"
                        >
                          {colName === "check" && isSelectable ? (
                            <Checkbox
                              isChecked={table.getIsAllRowsSelected()}
                              onChange={() => table.toggleAllRowsSelected()}
                            />
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                        </Text>
                        {colName !== "check" && header.column.getCanSort() && (
                          <chakra.span pl="2">
                            {{
                              desc: <FiChevronUp aria-label="sorted descending" />,
                              asc: <FiChevronDown aria-label="sorted ascending" />,
                              false: <FiChevronUp aria-label="sorted descending" />,
                            }[header.column.getIsSorted() || "false"]}
                          </chakra.span>
                        )}

                      </HStack>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel()?.rows?.map((row) => (
              <Tr
                key={`tr-${row.id}`}
                onClick={() => (handleLink ? handleLink(row) : {})}
                _hover={
                  handleLink && {
                    background: "gray.50",
                    cursor: "pointer",
                  }
                }
              >
                {row.getVisibleCells()?.map((cell, index) => {
                  const meta = cell.column.columnDef.meta;
                  const colName = cell.column.id;
                  if (cell.column.columnDef?.isHidden) return null;
                  return (
                    <Td
                      maxWidth={cell.column.columnDef?.maxWidth}
                      key={cell.id}
                      py={simplify ? "1" : "3"}
                      overflow="hidden"
                      isNumeric={meta?.isNumeric}
                      bg={row.getIsSelected() ? "purple.50" : "white"}
                    >
                      {colName === "check" && isSelectable ? (
                        <Checkbox
                          isChecked={row.getIsSelected()}
                          onChange={() => row.toggleSelected()}
                          disabled={!row.getCanSelect()}
                        />
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )
                      )}
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {!simplify && <Paginator table={table} />}
    </Flex>
  ) : (
    <EmptyState
      title="No Data"
      description="There is no data to display"
      actions={<>{otherComponents && otherComponents}</>}
    />
  );
};

export default DataTable;
